import { zodResolver } from '@hookform/resolvers/zod'
import dayjs from 'dayjs'
import { Dispatch, SetStateAction } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { z } from 'zod'

import { Button } from '@/modules/shared/components/button/Button'
import FormInput from '@/modules/shared/components/create-form/FormInput'
import { Modal } from '@/modules/shared/components/modal/ModalComponent'
import { ColumnDefinition } from '@/modules/shared/components/table/types'
import { useAlert } from '@/modules/shared/hooks/useAlert'

interface DownloadAsCSVModalProps<T> {
  showModal: boolean
  setShowModal: Dispatch<SetStateAction<boolean>>
  columns: ColumnDefinition<T>[]
  dataSource: T[]
  tableName?: string
}

const downloadAsCSVSchema = z.object({
  fileName: z.string().min(1),
})

const escapeCSVValue = (value: string): string => {
  if (!value) return ''

  // Check if the value contains special characters that require quoting
  const needsQuoting = /[",\r\n]/.test(value)

  if (needsQuoting) {
    // Escape existing quotes by doubling them and wrap in quotes
    return `"${value.replace(/"/g, '""')}"`
  }

  return value
}

const generateCSVContent = <T,>(columns: ColumnDefinition<T>[], dataSource: T[]): string => {
  const columnsToDownload = columns?.filter((col) => !col.hidden && col.key !== 'more')

  if (!columnsToDownload?.length) {
    throw new Error('No columns available for download')
  }

  const headers = columnsToDownload.map((col) => escapeCSVValue(col.title || ''))
  const rows = dataSource?.map((row, index) => {
    return columnsToDownload
      .map((col) => {
        const cellValue = col.renderExport?.(row, index) ?? ''
        return escapeCSVValue(String(cellValue))
      })
      .join(',')
  })

  return [headers.join(','), ...(rows || [])].join('\n')
}

const downloadCSVFile = (csvContent: string, fileName: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)

  try {
    const link = document.createElement('a')
    link.href = url
    link.download = `${fileName}.csv`
    link.style.display = 'none'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } finally {
    // Clean up the object URL to prevent memory leaks
    URL.revokeObjectURL(url)
  }
}

export default function DownloadAsCSVModal<T>({
  showModal,
  setShowModal,
  columns,
  dataSource,
  tableName,
}: DownloadAsCSVModalProps<T>) {
  const { t } = useTranslation()
  const { alertDialog } = useAlert()
  const formMethods = useForm<z.infer<typeof downloadAsCSVSchema>>({
    resolver: zodResolver(downloadAsCSVSchema),
    defaultValues: {
      fileName: `${tableName || 'results'}-${dayjs().format('YYYY-MM-DD')}`,
    },
  })

  const onCloseModal = () => {
    setShowModal(false)
  }

  const onSubmit = (data: z.infer<typeof downloadAsCSVSchema>) => {
    try {
      if (!dataSource?.length) {
        console.warn('No data available for download')
        return
      }
      const csvContent = generateCSVContent(columns, dataSource)
      downloadCSVFile(csvContent, data.fileName)

      onCloseModal()
    } catch {
      alertDialog({
        title: t('general.failedToDownload', 'Failed to download'),
        message: t('general.anErrorOccurredWhileDownloadingCsv', 'An error occurred while downloading the CSV file.'),
        type: 'error',
      })
    }
  }

  return (
    <Modal showModal={showModal} onCloseModal={onCloseModal}>
      <Modal.Panel
        as="form"
        onSubmit={formMethods.handleSubmit(onSubmit)}
        className="flex w-full flex-col overflow-hidden rounded-md bg-white shadow-xl transition-all md:max-w-[37.5rem]"
      >
        <Modal.Title title={t('general.downloadPageResults', 'Download Page Results')} onCloseModal={onCloseModal} />

        <Modal.Body className="space-y-4">
          <section className="rounded-md bg-gray-200 p-4">
            <p className="text-sm">
              <Trans t={t} i18nKey="downloadAsCSVModal.downloadPageResultsDescription">
                Here you can download the current page results as a CSV File. Only the current page results will be
                included in the CSV File but we are planning to improve this in the future.
              </Trans>
            </p>
          </section>
          <FormProvider {...formMethods}>
            <FormInput
              title={t('general.csvFileName', 'CSV File Name')}
              description={t(
                'general.input.csvFileNameDescription',
                'Enter the name of the CSV File that will be downloaded.'
              )}
              register="fileName"
              placeholder={t('general.csvFileName', 'CSV File Name')}
              maxLength={150}
              requiredError={t('general.input.csvFileNameRequiredError', 'Filename is required')}
            />
          </FormProvider>
        </Modal.Body>
        <Modal.Footer className="flex flex-col-reverse items-center justify-end gap-2 md:flex-row">
          <Button
            type="button"
            className="h-11 w-full rounded-md bg-gray-200 px-5 text-sm md:w-fit"
            onClick={onCloseModal}
          >
            {t('general.cancel', 'Cancel')}
          </Button>

          <Button type="submit" className="h-11 w-full rounded-md bg-primary px-5 text-sm text-white md:w-fit">
            {t('general.download', 'Download')}
          </Button>
        </Modal.Footer>
      </Modal.Panel>
    </Modal>
  )
}
